import React, { useContext, useRef, useState } from 'react'
import { useField } from 'formik'
import { useLingui } from '@lingui/react/macro'

import { Select, styled } from 'tamagui'

// import { Icon } from '../../components/Icon'
import { Icon } from '../Icon'
import { FormFieldContext } from './shared'

// import './FormSelect.css'

type StyledSelectProps = React.ComponentProps<typeof Select>

export const StyledSelect = ({ children, ...props }: StyledSelectProps) => {
  return <Select {...props}>{children}</Select>
}

StyledSelect.displayName = 'StyledSelect'

type FormSelectProps = StyledSelectProps & {
  items: Array<{ value: string; text: string }>
  onValueChange?: (value: string) => void
  width?: React.ComponentProps<typeof StyledTrigger>['width']
}

export const FormSelect = ({
  items,
  width,
  onValueChange,
  ...props
}: FormSelectProps) => {
  const { t } = useLingui()
  const formFieldContext = useContext(FormFieldContext)
  const [field, , helpers] = useField<string>(formFieldContext.name)

  // Controlled open state to prevent phantom re-opens on mobile.
  // When a user taps a dropdown item that overlaps the trigger position,
  // the browser synthesizes a click event after touchend. If the dropdown
  // is removed before that click fires, it lands on the trigger and
  // re-opens the dropdown. Using controlled state lets us block re-opens
  // for a brief window after selection.
  const [open, setOpen] = useState(false)
  const preventOpenRef = useRef(false)

  return (
    <StyledSelect
      value={field.value}
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen && preventOpenRef.current) return
        setOpen(nextOpen)
      }}
      onValueChange={(value) => {
        void helpers.setValue(value)
        onValueChange?.(value)

        preventOpenRef.current = true
        setTimeout(() => {
          preventOpenRef.current = false
        }, 300)
      }}
      {...props}
    >
      <StyledTrigger width={width}>
        <Select.Value placeholder={t`Select`} />
      </StyledTrigger>

      <StyledSelectContent>
        <Select.ScrollUpButton />
        <StyledSelectViewport minWidth={width}>
          <MemoizedSelectItems items={items} />
        </StyledSelectViewport>
        <Select.ScrollDownButton />
      </StyledSelectContent>
    </StyledSelect>
  )
}

FormSelect.displayName = 'FormSelect'

const MemoizedSelectItems = React.memo(
  ({ items }: { items: Array<{ value: string; text: string }> }) => (
    <>
      {items.map((item, index) => (
        <StyledSelectItem key={item.value} index={index} value={item.value}>
          <StyledSelectItemText borderRadius={4} flex={1}>
            {item.text}
          </StyledSelectItemText>

          <Select.ItemIndicator>
            <Icon name='checkmark' size='$5' color='$colorStrong' />
          </Select.ItemIndicator>
        </StyledSelectItem>
      ))}
    </>
  )
)
MemoizedSelectItems.displayName = 'MemoizedSelectItems'

export const StyledSelectContent = styled(Select.Content, {
  borderWidth: 1,

  borderRadius: '$2',

  borderColor: '$borderColor',

  // @ts-expect-error color is passed to styled children
  color: '$color',

  // High z-index to render above Dialog portals
  zIndex: 200_000,
})

export const StyledSelectItem = styled(Select.Item, {
  outlineStyle: 'unset',

  focusStyle: {
    outlineStyle: 'unset',
  },

  hoverStyle: {
    backgroundColor: '$backgroundHover',
    // @ts-expect-error color is passed to styled children
    color: '$color',
    cursor: 'pointer',
  },
})

export const StyledSelectItemText = styled(Select.ItemText, {
  color: 'inherit',
})

export const StyledTrigger = styled(Select.Trigger, {
  borderWidth: 1,
  borderRadius: '$2',
  color: '$color',

  borderColor: '$borderColor',
})

export const StyledSelectViewport = styled(Select.Viewport, {
  borderColor: '$borderColor',
})
